/**
 * Every URL the client SPA answers to, declared once: the fixed public paths,
 * the two store-scoped page segments, and the builders that turn a page and a
 * store code into a real URL. `routes-map.tsx` declares its routes from these
 * and every component that navigates reads them, so renaming a page is an edit
 * to the segment below and to nothing else.
 *
 * A path literal copied into a component is not a 404 waiting to happen, which
 * is why it survives review: renaming a public route leaves `<Navigate to>`
 * pointing at nothing, the URL falls through to the catch-all, the catch-all
 * sends it home, and the guard sends it back to the login path it reads from the
 * table — a redirect LOOP, with no failing request to point at it.
 *
 * This module imports nothing on purpose. The route table builds its elements at
 * module scope, so a component importing the table back would close a cycle that
 * blows up rather than resolving quietly; the definitions live here instead,
 * where both sides can reach them.
 */

/** `/login` — the sign-in screen, and where every expired session lands. */
export const LOGIN_PATH = '/login'

/** `/forgot-password` — asks for the email that gets the reset link. */
export const FORGOT_PASSWORD_PATH = '/forgot-password'

/** `/reset-password` — redeems the token from that link. */
export const RESET_PASSWORD_PATH = '/reset-password'

/** `/` — the entry point; it renders no page of its own, it forwards to a store. */
export const HOME_PATH = '/'

/** The URL segment of the weekly proposal page — the only place it is spelled. */
export const PROPOSAL_PAGE = 'propuesta'

/** The URL segment of the order history page — the only place it is spelled. */
export const ORDER_PAGE = 'pedidos'

/**
 * The store-scoped pages; the store code is always the 2nd path segment. The
 * type is derived from the list so adding a page is a one-line change.
 */
export const STORE_PAGES = [PROPOSAL_PAGE, ORDER_PAGE] as const
export type StorePage = (typeof STORE_PAGES)[number]

/** The page a store-scoped navigation lands on when the URL names none. */
export const DEFAULT_STORE_PAGE: StorePage = PROPOSAL_PAGE

/**
 * `/propuesta` — the page before a store is known. RequireStore forwards it to
 * the user's default store, so links fall back to it while one resolves.
 */
export const storePageBasePath = (page: StorePage): string => `/${page}`

/**
 * `/propuesta/mad` — the canonical URL of a store-scoped page. Every link,
 * redirect and navigation goes through here rather than interpolating a path,
 * so the shape of a store URL is written in one expression.
 */
export const storePagePath = (page: StorePage, storeCode: string): string =>
  `${storePageBasePath(page)}/${storeCode}`

/**
 * The pattern the router matches for a store-scoped page, built from the same
 * function as the real URLs so the two cannot drift apart.
 */
export const storePagePattern = (page: StorePage): string => storePagePath(page, ':storeCode')

/**
 * `/propuesta/mad` — the weekly proposal for one store.
 *
 * This and its siblings below are what a component calls: it names the SCREEN it
 * is linking to and never writes a URL, so the segment stays in `STORE_PAGES`
 * and renaming it is an edit here instead of a sweep over every call site. The
 * generic `storePagePath` remains for the two callers that take the page as a
 * value (the route table, and switching store on whatever page is in view).
 */
export const proposalPath = (storeCode: string): string => storePagePath(PROPOSAL_PAGE, storeCode)

/** `/pedidos/mad` — one store's order history. */
export const ordersPath = (storeCode: string): string => storePagePath(ORDER_PAGE, storeCode)

/** `/propuesta` — the proposal before a store is known. */
export const PROPOSAL_BASE_PATH = storePageBasePath(PROPOSAL_PAGE)

/** `/pedidos` — the history before a store is known. */
export const ORDERS_BASE_PATH = storePageBasePath(ORDER_PAGE)

/** `/pedidos/mad/2001` — one order's detail: the history URL plus the order. */
export const orderDetailPath = (storeCode: string, orderId: number): string =>
  `${ordersPath(storeCode)}/${orderId}`

/** The pattern for the order detail: the list pattern plus the order segment. */
export const ORDER_DETAIL_PATTERN = `${storePagePattern(ORDER_PAGE)}/:orderId`
